import { Injectable } from '@angular/core';
// Store
import { Store } from '@ngrx/store';
import { format } from 'date-fns';
import { get as _get } from 'lodash';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import {
  ApplyAutoPayDiscountPayload,
  AutoPayActions,
  AutoPaySelectors
} from '@amfam/billing/auto-pay/data-access';
import { PaymentMethodSelectors } from '@amfam/billing/payment-method/data-access';
import { PaymentMethod } from '@amfam/billing/payment-method/util';
import { PolicySelectors } from '@amfam/policy/data-access';
import { Policy } from '@amfam/policy/models';
import { AutomaticPayment } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { UtilService } from '@amfam/shared/utility/shared-services';
import { LoadingSpinnerService, ToasterService } from '@amfam/ui-kit';

@Injectable({ providedIn: 'root' })
export class AutoPayDeleteUtilService {
  constructor(
    private store: Store,
    private spinner: LoadingSpinnerService,
    private utilService: UtilService,
    private toasterService: ToasterService
  ) {}

  deleteAutoPay(automaticPayment: AutomaticPayment): void {
    this.spinner.start({
      blockActions: true
    });

    const correlationId: string = this.utilService.generateId();

    this.store.dispatch(
      AutoPayActions.deleteAutoPay({
        correlationId,
        payload: { billAccountNumber: automaticPayment.billAccountNumber }
      })
    );

    this.store
      .select(AutoPaySelectors.selectCorrelationId(automaticPayment.billAccountNumber))
      .pipe(
        filter(id => id === correlationId),
        take(1)
      )
      .subscribe(() => {
        this.spinner.stop();
        this.removeAutoPayDiscount(automaticPayment);
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['billing', 'auto-pay', 'detail', 'delete-confirmation'],
            query: { billAccount: automaticPayment.billAccountNumber }
          })
        );
      });
  }

  deleteAutoPayment(automaticPayment: AutomaticPayment): void {
    this.spinner.start({
      blockActions: true
    });

    const correlationId: string = this.utilService.generateId();

    this.store.dispatch(
      AutoPayActions.deleteAutoPay({
        correlationId,
        payload: { billAccountNumber: automaticPayment.billAccountNumber }
      })
    );

    this.store
      .select(AutoPaySelectors.selectCorrelationId(automaticPayment.billAccountNumber))
      .pipe(
        filter(id => id === correlationId),
        take(1)
      )
      .subscribe(() => {
        this.spinner.stop();
        this.removeAutoPayDiscount(automaticPayment);
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['billing', 'auto-pay', 'detail', 'delete-confirmation'],
            query: { billAccount: automaticPayment.billAccountNumber }
          })
        );
      });
  }

  deleteAutoPayUtil(automaticPayment: AutomaticPayment): void {
    this.spinner.start({
      blockActions: true
    });

    const correlationId: string = this.utilService.generateId();

    this.store.dispatch(
      AutoPayActions.deleteAutoPay({
        correlationId,
        payload: { billAccountNumber: automaticPayment.billAccountNumber }
      })
    );

    this.store
      .select(AutoPaySelectors.selectCorrelationId(automaticPayment.billAccountNumber))
      .pipe(
        filter(id => id === correlationId),
        take(1)
      )
      .subscribe(() => {
        this.spinner.stop();
        this.removeAutoPayDiscount(automaticPayment);
        this.confirmDeleteAutopay(automaticPayment.billAccountNumber);
        this.store.dispatch(
          fromRouterActions.Go({
            path: ['billing', 'manage-autopay']
          })
        );
      });
  }

  private confirmDeleteAutopay(billAccountNumber) {
    const hasError$ = this.store.select(
      AutoPaySelectors.selectAutoPayNotificationHasError(billAccountNumber)
    );
    hasError$.subscribe(data => {
      if (!data) {
        this.toasterService.pop('success', 'Autopay deleted succesfully');
      } else {
        this.toasterService.pop('error', 'Autopay deleted failed');
      }
    });
  }

  private removeAutoPayDiscount(automaticPayment: AutomaticPayment): void {
    combineLatest([
      this.store.select(PolicySelectors.selectActivePolicies),
      this.store.select(userQuery.selectCustomerId),
      this.store.select(BrandSelectors.selectPartnerId),
      this.store.select(PaymentMethodSelectors.selectPaymentMethods),
      this.store.select(AutoPaySelectors.getAutoPayNotifications)
    ])
      .pipe(take(1))
      .subscribe(([policies, customerId, partnerId, paymentMethods, notifications]) => {
        const billAccountNumber: string = automaticPayment.billAccountNumber;
        const hasError: boolean = notifications.find(
          notification => notification.id === billAccountNumber
        ).hasError;

        if (!hasError) {
          const paymentMethod: PaymentMethod = paymentMethods.find(
            payment_method => payment_method.nickName === automaticPayment.paymentAccount.nickName
          );
          const paymentMethodType: string = _get(
            paymentMethod,
            'achWithdrawal.accountType',
            'card'
          );
          const billAccountPolicies = policies
            .filter(policy => policy.billingAccountNumber === billAccountNumber)
            .map((policy: Policy) => ({
              policyNumber: policy.policyNumber,
              producerId: _get(policy, 'assignedProducer.producerIdNum', '')
            }));
          const payload: ApplyAutoPayDiscountPayload = {
            FormInstanceTypeAutoPay: {
              policies: billAccountPolicies,
              billAccount: billAccountNumber,
              paymentType: paymentMethodType,
              autoPayIndicator: false,
              effectiveDate: format(new Date(), 'YYYY-MM-DD'),
              customerId,
              partnerId,
              templateCode: 'AUTO_PAY_CHANGE'
            }
          };
          this.store.dispatch(
            AutoPayActions.applyAutoPayDiscount({
              payload
            })
          );
        }
      });
  }
}
